/**
 * ExportOptions.jsx — how the shareable video is shaped: frame ratio and
 * caption treatment. Both are persisted with the project.
 */
import React from "react";
import * as L from "lucide-react";
import { ASPECTS, CAPTION_STYLES } from "@/lib/studio-audiogram";

const ASPECT_HINTS = {
  "1:1": "Feeds, X, LinkedIn",
  "9:16": "Reels, Shorts, Stories",
  "16:9": "YouTube, presentations",
};

const CAPTION_HINTS = {
  classic: "Bold block of text",
  karaoke: "Word lights up as spoken",
  minimal: "Quiet line, thin waveform",
};

export function ExportOptions({ settings, onChange }) {
  const aspect = settings?.aspect || "1:1";
  const captionStyle = settings?.caption_style || "classic";

  return (
    <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4 space-y-4" data-testid="studio-export-options">
      <div className="flex items-center gap-2">
        <L.Film size={14} className="text-[var(--rammy-cyan-dark)]"/>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">Video export</div>
        <span className="text-[11px] text-[var(--rammy-text-muted)]">Shape of the shareable clip</span>
      </div>

      <div>
        <div className="text-[11px] text-[var(--rammy-text-muted)] mb-2">Frame</div>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(ASPECTS).map(([id, meta]) => {
            const active = aspect === id;
            const ratio = meta.w / meta.h;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onChange({ ...settings, aspect: id })}
                data-active={active ? "true" : "false"}
                data-testid={`studio-aspect-${id.replace(":", "-")}`}
                className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 transition-colors ${
                  active
                    ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12"
                    : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
                }`}
              >
                <span
                  className={`block rounded-[3px] border-2 ${active ? "border-[var(--rammy-cyan-dark)]" : "border-[var(--rammy-text-muted)]"}`}
                  style={{ width: ratio >= 1 ? 26 : 26 * ratio, height: ratio >= 1 ? 26 / ratio : 26 }}
                />
                <span className="text-left">
                  <span className="block text-[12px] font-medium">{id}</span>
                  <span className="block text-[10px] text-[var(--rammy-text-muted)]">{ASPECT_HINTS[id]}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[11px] text-[var(--rammy-text-muted)] mb-2">Captions</div>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(CAPTION_STYLES).map(([id, meta]) => {
            const active = captionStyle === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onChange({ ...settings, caption_style: id })}
                data-active={active ? "true" : "false"}
                data-testid={`studio-caption-${id}`}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  active
                    ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12"
                    : "border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)]"
                }`}
              >
                <span className="block text-[12px] font-medium">{meta.label}</span>
                <span className="block text-[10px] text-[var(--rammy-text-muted)]">{CAPTION_HINTS[id]}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
